import { RefObject, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useLatest } from 'ahooks';
import { FULLSCREEN_META_CFG } from '../constants';

interface IFullscreenOptions {
  requestFullscreen: string;
  exitFullscreen: string;
  fullscreenElement: string;
  fullscreenEnabled: string;
  fullscreenchange: string;
  fullscreenerror: string;
}

export default function useFullScreen(
  fullScreenContent: RefObject<HTMLElement | null>,
  onFullScreenChange?: (isFullScreen: boolean) => void,
  setIsFullScreenOut?: any,
) {
  const handleFullScreenChangeHandler = useLatest<((isFullScreen: boolean) => void) | undefined>(onFullScreenChange);

  // 是否全屏
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const isFullScreenRef = useRef(isFullScreen);
  isFullScreenRef.current = isFullScreen;

  const element = useMemo<IFullscreenOptions>(() => {
    if ('webkitRequestFullscreen' in document.documentElement) return FULLSCREEN_META_CFG.WEBKIT;
    if ('mozRequestFullScreen' in document.documentElement) return FULLSCREEN_META_CFG.MOZ;
    if ('msRequestFullscreen' in document.documentElement) return FULLSCREEN_META_CFG.MS;
    return FULLSCREEN_META_CFG.DEFAULT;
  }, []);

  // 进入全屏——先设状态触发渲染，等 React 完成绘制后再调浏览器 API
  const requestFullScreen = () => {
    if (fullScreenContent.current) {
      setIsFullScreen(true);
      handleFullScreenChangeHandler.current?.(true);
      setIsFullScreenOut?.(true);
      const el = fullScreenContent.current;
      setTimeout(() => {
        // @ts-ignore
        el[element.requestFullscreen]?.();
      }, 100);
    }
  };

  // 退出全屏——先设状态触发渲染，等 React 完成绘制后再调浏览器 API
  const exitFullScreen = useCallback(() => {
    setIsFullScreen(false);
    handleFullScreenChangeHandler.current?.(false);
    setIsFullScreenOut?.(false);
    setTimeout(() => {
      // @ts-ignore
      document[element.exitFullscreen]?.();
    }, 100);
  }, [element, handleFullScreenChangeHandler, setIsFullScreenOut]);

  // 切换是否全屏
  const handleFullScreenChange = useCallback(() => {
    // @ts-ignore
    const isFullScreenNew = !!document[element.fullscreenElement];
    setIsFullScreen(isFullScreenNew);
    handleFullScreenChangeHandler.current?.(isFullScreenNew);
    // 保持与 exitFullScreen 路径一致：Esc 键退出时也同步对外状态，
    // 让父组件能够执行恢复滚动位置等收尾逻辑
    setIsFullScreenOut?.(isFullScreenNew);
  }, [element, handleFullScreenChangeHandler, setIsFullScreenOut]);

  useEffect(() => {
    document.addEventListener(element.fullscreenchange, handleFullScreenChange);

    return () => {
      document.removeEventListener(element.fullscreenchange, handleFullScreenChange);
    };
  }, [element, handleFullScreenChange]);

  return { isFullScreen, requestFullScreen, exitFullScreen };
}
